import { ItemBadge } from '@liveauctioneers/hammer-ui-core/badge/ItemBadge';
import { cn } from '@liveauctioneers/hammer-ui-theme/cn';

import ConsoleSocialBadge from '@/components/ConsoleSocial/ConsoleSocialBadge';
import { useAppSelector } from '@/redux/hooks';
import { getCatalogAbsenteeBidders } from '@/redux/modules/catalog/catalogs/catalog.selectors';
import { getLiveCatalogBidders, getLiveCatalogPaused } from '@/redux/modules/console';
import {
    getItemSummaryCatalogId,
    getItemSummaryCatalogStatus,
    getItemSummaryIsCatalogOnly,
    getItemSummaryIsLocked,
    getItemSummaryIsPassed,
    getItemSummaryIsSold,
    getItemSummaryPromoted,
    getItemSummarySaleStartTs,
} from '@/redux/modules/item/summary/itemSummary.selectors';
import { getLiveItemStatusOpen } from '@/redux/modules/liveItemStatus';
import { shouldShowLive } from '@/utils/live';

export type ItemCardBadgeProps = {
    allowBidding?: boolean;
    className?: string;
    itemId: number;
    myItemsSavedItemCard?: boolean;
    pushBadgeToLeftCorner?: boolean;
    savedItemsCard?: boolean;
};

const ItemCardBadge = ({
    allowBidding,
    className,
    itemId,
    myItemsSavedItemCard,
    pushBadgeToLeftCorner,
    savedItemsCard,
}: ItemCardBadgeProps) => {
    const catalogId = useAppSelector((state) => getItemSummaryCatalogId(state, itemId));
    const catalogStatus = useAppSelector((state) => getItemSummaryCatalogStatus(state, itemId));
    const isCatalogOnly = useAppSelector((state) => getItemSummaryIsCatalogOnly(state, itemId));
    const isLocked = useAppSelector((state) => getItemSummaryIsLocked(state, itemId));
    const isPassed = useAppSelector((state) => getItemSummaryIsPassed(state, itemId));
    const isSold = useAppSelector((state) => getItemSummaryIsSold(state, itemId));
    const promoted = useAppSelector((state) => getItemSummaryPromoted(state, itemId));
    const saleStartTs = useAppSelector((state) => getItemSummarySaleStartTs(state, itemId));
    const isLotOpen = useAppSelector((state) => getLiveItemStatusOpen(state, itemId));
    const isUnsold = isLocked && !isPassed && !isSold && !isLotOpen;
    const bidders = useAppSelector((state) => getLiveCatalogBidders(state, catalogId));
    const absenteeBidders = useAppSelector((state) => getCatalogAbsenteeBidders(state, catalogId));
    const paused = useAppSelector((state) => getLiveCatalogPaused(state, catalogId));
    const showLive = shouldShowLive(catalogStatus, isCatalogOnly, saleStartTs);
    const showSocial = allowBidding && showLive && !isSold && !isPassed;
    const viewers = bidders + absenteeBidders || 1;

    const itemBadgeClassName = cn(
        'absolute z-[2]',
        {
            'xsMax:top-2xs xsMax:left-2xs': pushBadgeToLeftCorner,
        },
        className
    );

    return (
        <>
            {!isSold && promoted && (
                <ItemBadge
                    className={itemBadgeClassName}
                    isFeatured
                />
            )}
            {isSold && !isPassed && (
                <ItemBadge
                    className={itemBadgeClassName}
                    isSold
                />
            )}
            {showSocial && !isSold && !isUnsold && (
                <ConsoleSocialBadge
                    className={cn(
                        'absolute top-0 m-[10px]',
                        {
                            'mt-xs ml-xs xsMax:ml-2xs xsMax:pr-[6px]': myItemsSavedItemCard,
                            'mt-xs ml-xs xsMax:mt-[12px] xsMax:ml-xs': savedItemsCard,
                        },
                        className
                    )}
                    myItemsSavedItemsCard={myItemsSavedItemCard}
                    pause={paused}
                    viewers={viewers}
                />
            )}
        </>
    );
};

export default ItemCardBadge;
